import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { RootState } from 'app/store';
import { axiosPrivate } from 'app/utils';

export type OrganisationPlan = 'starter' | 'plus' | 'bespoke' | 'legacy-starter' | 'legacy-pro';

export type OrganisationFeature =
  | 'assistedOrdering'
  | 'marketingMaterial'
  | 'pgds'
  | 'smsCampaigns'
  | 'superLinks'
  | 'whatIsNew'
  | 'digitalManagedRepeats'
  | 'webBuilderAccessEnabled'
  | 'hasBespoke'
  | 'segmentedBroadcast'
  | 'promotionalBroadcasts'
  | 'promotionalSmsCampaigns'
  | 'superLinkConversions'
  | 'inAppBanners';

interface GetOrganisationFeaturesResponse {
  organisationId: string;
  plan: OrganisationPlan;
  features: Record<OrganisationFeature, boolean | null>;
  pricingLaunched: boolean;
  plusUpgradeRequestedAt: string | null;
}

const FEATURES_FRESH_FOR_MS = 2 * 60 * 1000;

export const ORGANISATION_FEATURES_QUERY_KEY = 'organisation-features';

export const organisationFeaturesQueryKey = (organisationId?: string): (string | undefined)[] => [
  ORGANISATION_FEATURES_QUERY_KEY,
  organisationId,
];

function getOrganisationFeatures(organisationId: string) {
  return axiosPrivate.get<GetOrganisationFeaturesResponse>(
    `/v3/pharmacist/organisations/${organisationId}/features`
  );
}

export interface UseOrganisationFeaturesResult {
  plan: OrganisationPlan | undefined;
  hasFeature: (feature: OrganisationFeature) => boolean;
  isFeatureHidden: (feature: OrganisationFeature) => boolean;
  pricingLaunched: boolean;
  /** The organisation has asked account management for Plus, so it is not asked to upgrade again. */
  plusUpgradeRequested: boolean;
  isLoading: boolean;
  /** The plan features could not be loaded, or there is no organisation to load them for. */
  isError: boolean;
  retry: () => void;
}

export function useOrganisationFeaturesOf(
  organisationId: string | undefined
): UseOrganisationFeaturesResult {
  const { data, isInitialLoading, isError, isFetching, refetch } = useQuery({
    queryKey: organisationFeaturesQueryKey(organisationId),
    queryFn: () => getOrganisationFeatures(organisationId ?? ''),
    enabled: !!organisationId,
    staleTime: FEATURES_FRESH_FOR_MS,
  });

  const features = data?.data.features;
  // A retry is a fetch like the first one, so a page shows progress rather than the stale error.
  const retrying = isError && isFetching;

  return {
    plan: data?.data.plan,
    hasFeature: (feature) => features?.[feature] === true,
    isFeatureHidden: (feature) => features?.[feature] === false,
    pricingLaunched: data?.data.pricingLaunched === true,
    plusUpgradeRequested: !!data?.data.plusUpgradeRequestedAt,
    isLoading: isInitialLoading || retrying,
    isError: !organisationId || (isError && !retrying),
    retry: () => {
      if (organisationId) refetch();
    },
  };
}

export default function useOrganisationFeatures(): UseOrganisationFeaturesResult {
  const { selectedPharmacy, pharmacies, fetching } = useSelector(
    (state: RootState) => state.pharmacySelector
  );
  const organisationId = selectedPharmacy?.chain_id;
  const result = useOrganisationFeaturesOf(organisationId);

  // There is no organisation to ask about until the pharmacy selector has settled: the user's
  // pharmacies are still being fetched, or they have arrived and the first one is being selected.
  const selectingPharmacy =
    !organisationId &&
    (pharmacies === undefined || fetching || (pharmacies.length > 0 && !selectedPharmacy));

  if (selectingPharmacy) return { ...result, isLoading: true, isError: false };
  return result;
}
